import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Switch, TextInput, ActivityIndicator, Keyboard, Modal, KeyboardAvoidingView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Alert } from '../../utils/toast';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { updateClubSettings, leaveClub, submitExitRequest, updateClubCode } from '../../lib/api/clubs';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import * as Clipboard from 'expo-clipboard';

interface ClubDetailsTabProps {
  clubId: string;
  isAdmin: boolean;
  isCaptain?: boolean;
  membershipStatus?: string;
  initialChatEnabled: boolean;
  initialDescription: string;
  initialRules: string;
  initialAllowedPostingDays?: number[];
  initialCode?: string;
  isPaid?: boolean;
  annualFee?: number;
  onSettingsUpdated?: () => void;
}

const ClubDetailsTab: React.FC<ClubDetailsTabProps> = ({
  clubId,
  isAdmin,
  isCaptain = false,
  membershipStatus,
  initialChatEnabled,
  initialDescription,
  initialRules,
  initialAllowedPostingDays = [],
  initialCode = '',
  isPaid = false,
  annualFee,
  onSettingsUpdated,
}) => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const [chatEnabled, setChatEnabled] = useState(initialChatEnabled);
  const [description, setDescription] = useState(initialDescription);
  const [rules, setRules] = useState(initialRules);
  const [allowedPostingDays, setAllowedPostingDays] = useState<number[]>(initialAllowedPostingDays);
  const [clubCode, setClubCode] = useState(initialCode);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editedCode, setEditedCode] = useState(initialCode);
  const [isSavingCode, setIsSavingCode] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitNote, setExitNote] = useState('');
  const [submittingExit, setSubmittingExit] = useState(false);

  // Update clubCode when initialCode changes
  useEffect(() => {
    console.log('ClubDetailsTab - Initial Code received:', initialCode, 'isAdmin:', isAdmin, 'isCaptain:', isCaptain);
    if (initialCode) {
      setClubCode(initialCode);
      setEditedCode(initialCode);
    }
  }, [initialCode, isAdmin, isCaptain]);

  const DAYS = [
    { label: 'Sun', value: 0 },
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
  ];

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', (e) => {
      setKeyboardVisible(true);
      setKeyboardHeight(e.endCoordinates.height);
    });
    const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);
      setKeyboardHeight(0);
    });

    return () => {
      keyboardDidShowListener.remove();
      keyboardDidHideListener.remove();
    };
  }, []);

  const handleToggleChat = async () => {
    const newValue = !chatEnabled;
    setIsSavingSettings(true);
    try {
      await updateClubSettings(clubId, { chatEnabled: newValue });
      setChatEnabled(newValue);
      Alert.alert('Success', `Group chat ${newValue ? 'enabled' : 'disabled'}`);
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error updating chat setting:', error);
      Alert.alert('Error', 'Failed to update chat setting. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const togglePostingDay = (dayValue: number) => {
    setAllowedPostingDays(prev => {
      if (prev.includes(dayValue)) {
        return prev.filter(d => d !== dayValue);
      } else {
        return [...prev, dayValue].sort();
      }
    });
  };

  const handleLeaveClub = async () => {
    setShowExitModal(true);
  };

  const handleSubmitExitRequest = async () => {
    setSubmittingExit(true);
    try {
      await submitExitRequest(clubId, exitNote.trim() || undefined);
      setShowExitModal(false);
      setExitNote('');
      showSuccessToast('Exit request submitted. Awaiting admin approval.');
      onSettingsUpdated?.();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to submit exit request');
    } finally {
      setSubmittingExit(false);
    }
  };

  const handleSavePostingDays = async () => {
    setIsSavingSettings(true);
    try {
      await updateClubSettings(clubId, { allowedPostingDays });
      Alert.alert('Success', 'Posting schedule updated');
      onSettingsUpdated?.();
    } catch (error) {
      console.error('Error updating posting days:', error);
      Alert.alert('Error', 'Failed to update posting schedule. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleCopyCode = async () => {
    if (clubCode) {
      await Clipboard.setStringAsync(clubCode);
      showSuccessToast('Club code copied to clipboard!');
    }
  };

  const handleEditCode = () => {
    setEditedCode(clubCode);
    setIsEditingCode(true);
  };

  const handleCancelEditCode = () => {
    setEditedCode(clubCode);
    setIsEditingCode(false);
  };

  const handleSaveCode = async () => {
    // Validate code format (must be exactly 8 digits)
    if (!/^\d{8}$/.test(editedCode)) {
      showErrorToast('Code must be exactly 8 digits');
      return;
    }

    setIsSavingCode(true);
    try {
      await updateClubCode(clubId, editedCode);
      setClubCode(editedCode);
      setIsEditingCode(false);
      showSuccessToast('Club code updated successfully');
      onSettingsUpdated?.();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to update club code');
    } finally {
      setIsSavingCode(false);
    }
  };

  const renderAdminSettings = () => {
    const canManage = isAdmin || isCaptain;
    if (!canManage) return null;

    return (
      <View style={styles.section}>
        <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
          Club Management
        </ThemedText>

        {/* Club Code Section - Admin and Captains */}
        {clubCode && (
          <View style={[styles.settingCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <View style={styles.codeSection}>
              <View style={styles.settingInfo}>
                <Ionicons name="key-outline" size={24} color={theme.accent} />
                <View style={styles.settingText}>
                  <ThemedText style={[styles.settingLabel, { color: theme.text }]}>
                    Club Code
                  </ThemedText>
                  <ThemedText style={[styles.settingDescription, { color: theme.textSecondary }]}>
                    Share this code with members
                  </ThemedText>
                </View>
              </View>
              
              {isEditingCode ? (
                <View style={styles.codeEditContainer}>
                  <TextInput
                    style={[styles.codeInput, { 
                      backgroundColor: theme.inputBackground, 
                      color: theme.text,
                      borderColor: theme.border 
                    }]}
                    value={editedCode}
                    onChangeText={setEditedCode}
                    keyboardType="number-pad"
                    maxLength={8}
                    placeholder="8-digit code"
                    placeholderTextColor={theme.textTertiary}
                  />
                  <View style={styles.codeEditButtons}>
                    <TouchableOpacity
                      style={[styles.codeEditButton, { backgroundColor: theme.border }]}
                      onPress={handleCancelEditCode}
                      disabled={isSavingCode}
                    >
                      <ThemedText style={[styles.codeEditButtonText, { color: theme.text }]}>
                        Cancel
                      </ThemedText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.codeEditButton, { backgroundColor: theme.accent }]}
                      onPress={handleSaveCode}
                      disabled={isSavingCode}
                    >
                      {isSavingCode ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <ThemedText style={[styles.codeEditButtonText, { color: '#FFFFFF' }]}>
                          Save
                        </ThemedText>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <View style={styles.codeDisplayContainer}>
                  <View style={[styles.codeBox, { backgroundColor: theme.inputBackground, borderColor: theme.border }]}>
                    <ThemedText style={[styles.codeText, { color: theme.text }]}>
                      {clubCode}
                    </ThemedText>
                  </View>
                  <View style={styles.codeActions}>
                    <TouchableOpacity
                      style={[styles.codeActionButton, { backgroundColor: theme.accent + '20', borderColor: theme.accent }]}
                      onPress={handleCopyCode}
                    >
                      <Ionicons name="copy-outline" size={20} color={theme.accent} />
                      <ThemedText style={[styles.codeActionText, { color: theme.accent }]}>
                        Copy
                      </ThemedText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.codeActionButton, { backgroundColor: theme.accent + '20', borderColor: theme.accent }]}
                      onPress={handleEditCode}
                    >
                      <Ionicons name="create-outline" size={20} color={theme.accent} />
                      <ThemedText style={[styles.codeActionText, { color: theme.accent }]}>
                        Edit
                      </ThemedText>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Chat Enable Toggle - Admin and Captains */}
        <View style={[styles.settingCard, { backgroundColor: theme.card, borderColor: theme.border, marginTop: clubCode ? 16 : 0 }]}>
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Ionicons name="chatbubbles-outline" size={24} color={theme.accent} />
              <View style={styles.settingText}>
                <ThemedText style={[styles.settingLabel, { color: theme.text }]}>
                  Enable Group Chat
                </ThemedText>
                <ThemedText style={[styles.settingDescription, { color: theme.textSecondary }]}>
                  Allow members to chat in the group
                </ThemedText>
              </View>
            </View>
            <Switch
              value={chatEnabled}
              onValueChange={handleToggleChat}
              disabled={isSavingSettings}
              trackColor={{ false: theme.border, true: theme.accent }}
              thumbColor="#FFFFFF"
            />
          </View>
        </View>

        {/* Posting Schedule Section - Admin and Captains */}
        <View style={[styles.settingCard, { backgroundColor: theme.card, borderColor: theme.border, marginTop: 16 }]}>
          <View style={styles.postingDaysSection}>
            <View style={styles.postingDaysHeader}>
              <View style={styles.settingInfo}>
                <Ionicons name="calendar-outline" size={24} color={theme.accent} />
                <View style={styles.settingText}>
                  <ThemedText style={[styles.settingLabel, { color: theme.text }]}>
                    Posting Schedule
                  </ThemedText>
                  <ThemedText style={[styles.settingDescription, { color: theme.textSecondary }]}>
                    {allowedPostingDays.length === 0 ? 'All days allowed' : 'Select allowed posting days'}
                  </ThemedText>
                </View>
              </View>
            </View>
            
            <View style={styles.daysContainer}>
              {DAYS.map(day => {
                const isSelected = allowedPostingDays.includes(day.value);
                const isAllDays = allowedPostingDays.length === 0;
                return (
                  <TouchableOpacity
                    key={day.value}
                    style={[
                      styles.dayButton,
                      { borderColor: theme.border },
                      (isSelected || isAllDays) && {
                        backgroundColor: theme.accent,
                        borderColor: theme.accent,
                      },
                    ]}
                    onPress={() => togglePostingDay(day.value)}
                    disabled={isSavingSettings}
                  >
                    <ThemedText
                      style={[
                        styles.dayButtonText,
                        { color: theme.text },
                        (isSelected || isAllDays) && { color: '#FFFFFF' },
                      ]}
                    >
                      {day.label}
                    </ThemedText>
                  </TouchableOpacity>
                );
              })}
            </View>

            <TouchableOpacity
              style={[
                styles.saveButton,
                { backgroundColor: theme.accent },
                isSavingSettings && { opacity: 0.6 },
              ]}
              onPress={handleSavePostingDays}
              disabled={isSavingSettings}
            >
              {isSavingSettings ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <ThemedText style={styles.saveButtonText}>Save Schedule</ThemedText>
              )}
            </TouchableOpacity>

            {allowedPostingDays.length > 0 && (
              <TouchableOpacity
                style={styles.clearButton}
                onPress={() => setAllowedPostingDays([])}
              >
                <ThemedText style={[styles.clearButtonText, { color: theme.accent }]}>
                  Allow All Days
                </ThemedText>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  };

  const renderDescription = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
          About
        </ThemedText>
      </View>

      <View style={[styles.contentCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        {description ? (
          <ThemedText style={[styles.contentText, { color: theme.text }]}>
            {description}
          </ThemedText>
        ) : (
          <ThemedText style={[styles.emptyText, { color: theme.textTertiary }]}>
            No description available
          </ThemedText>
        )}
      </View>
    </View>
  );

  const renderRules = () => (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
          Rules
        </ThemedText>
      </View>

      <View style={[styles.contentCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        {rules ? (
          <ThemedText style={[styles.contentText, { color: theme.text }]}>
            {rules}
          </ThemedText>
        ) : (
          <ThemedText style={[styles.emptyText, { color: theme.textTertiary }]}>
            No rules defined
          </ThemedText>
        )}
      </View>
    </View>
  );

  const renderLeaveClub = () => {
    // Only show leave button if user is a member and not an admin
    if (isAdmin || membershipStatus !== 'joined') return null;

    return (
      <View style={styles.leaveSection}>
        <View style={[styles.warningCard, { backgroundColor: theme.error + '10', borderColor: theme.error }]}>
          <Ionicons name="warning" size={24} color={theme.error} />
          <View style={styles.warningContent}>
            <ThemedText style={[styles.warningTitle, { color: theme.error }]}>
              Leave Club
            </ThemedText>
            <ThemedText style={[styles.warningText, { color: theme.textSecondary }]}>
              Once you leave, you'll need to request to join again. All your club activity will be removed.
            </ThemedText>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.leaveButton, { borderColor: theme.error }]}
          onPress={handleLeaveClub}
          activeOpacity={0.7}
        >
          <Ionicons name="exit-outline" size={20} color={theme.error} />
          <ThemedText style={[styles.leaveButtonText, { color: theme.error }]}>
            Leave Club
          </ThemedText>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {renderAdminSettings()}
        {/* Membership type info */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>Membership</ThemedText>
          </View>
          <View style={[styles.contentCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Ionicons
                name={isPaid ? 'card-outline' : 'lock-open-outline'}
                size={20}
                color={isPaid ? theme.primary : '#10B981'}
              />
              <View style={{ flex: 1 }}>
                <ThemedText style={[styles.contentText, { color: theme.text }]}>
                  {isPaid ? 'Paid Membership' : 'Free Membership'}
                </ThemedText>
                {isPaid && annualFee && (
                  <ThemedText style={{ color: theme.textSecondary, fontSize: 13, marginTop: 2 }}>
                    Annual fee: ₹{annualFee.toLocaleString('en-IN')} / year
                  </ThemedText>
                )}
              </View>
              <View style={[{
                paddingHorizontal: 10,
                paddingVertical: 4,
                borderRadius: 10,
                backgroundColor: isPaid ? theme.primary + '20' : '#10B98120',
              }]}>
                <ThemedText style={{ fontSize: 12, fontWeight: '700', color: isPaid ? theme.primary : '#10B981' }}>
                  {isPaid ? 'PAID' : 'FREE'}
                </ThemedText>
              </View>
            </View>
          </View>
        </View>
        {renderDescription()}
        {renderRules()}
        {renderLeaveClub()}
        {/* Empty space at bottom for keyboard visibility */}
        {keyboardVisible && <View style={styles.bottomSpacer} />}
      </ScrollView>

      {/* Exit Request Modal */}
      <Modal
        visible={showExitModal}
        transparent={false}
        animationType="slide"
        onRequestClose={() => setShowExitModal(false)}
      >
        <SafeAreaView style={[styles.fullScreenModal, { backgroundColor: theme.background }]} edges={['top', 'bottom']}>
          <KeyboardAvoidingView 
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.keyboardAvoidingView}
          >
            <View style={styles.fullScreenModalHeader}>
              <TouchableOpacity onPress={() => setShowExitModal(false)} style={styles.closeButton}>
                <Ionicons name="close" size={28} color={theme.text} />
              </TouchableOpacity>
              <ThemedText style={[styles.fullScreenModalTitle, { color: theme.text }]}>
                Request to Leave Club
              </ThemedText>
              <View style={styles.closeButton} />
            </View>

            <ScrollView 
              contentContainerStyle={styles.fullScreenModalContent}
              keyboardShouldPersistTaps="handled"
            >
              <ThemedText style={[styles.fullScreenModalDescription, { color: theme.textSecondary }]}>
                Please provide a reason for leaving. Your request will be reviewed by the admin.
              </ThemedText>

              <TextInput
                style={[
                  styles.fullScreenExitNoteInput,
                  {
                    backgroundColor: theme.card,
                    borderColor: theme.border,
                    color: theme.text,
                  },
                ]}
                placeholder="Why are you leaving? (Optional)"
                placeholderTextColor={theme.textTertiary}
                value={exitNote}
                onChangeText={setExitNote}
                multiline
                numberOfLines={6}
                textAlignVertical="top"
              />

              {/* Spacer when keyboard is visible */}
              {keyboardVisible && <View style={{ height: keyboardHeight }} />}
            </ScrollView>

            <View
              style={[
                styles.fullScreenModalFooter,
                {
                  backgroundColor: theme.background,
                  borderTopColor: theme.border,
                },
              ]}
            >
              <TouchableOpacity
                style={[styles.fullScreenCancelButton, { borderColor: theme.border }]}
                onPress={() => setShowExitModal(false)}
                disabled={submittingExit}
              >
                <ThemedText style={[styles.fullScreenCancelButtonText, { color: theme.text }]}>
                  Cancel
                </ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.fullScreenSubmitButton,
                  { backgroundColor: theme.error },
                  submittingExit && { opacity: 0.6 },
                ]}
                onPress={handleSubmitExitRequest}
                disabled={submittingExit}
              >
                {submittingExit ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <ThemedText style={styles.fullScreenSubmitButtonText}>Submit Request</ThemedText>
                )}
              </TouchableOpacity>
            </View>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  settingCard: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  settingText: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  settingDescription: {
    fontSize: 13,
    marginTop: 2,
  },
  divider: {
    height: 1,
  },
  contentCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  contentText: {
    fontSize: 15,
    lineHeight: 22,
  },
  emptyText: {
    fontSize: 15,
    fontStyle: 'italic',
  },
  leaveSection: {
    marginTop: 32,
    marginBottom: 16,
  },
  warningCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    gap: 12,
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  warningText: {
    fontSize: 14,
    lineHeight: 20,
  },
  leaveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    gap: 8,
  },
  leaveButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  textArea: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    fontSize: 15,
    minHeight: 120,
  },
  postingDaysSection: {
    padding: 16,
  },
  postingDaysHeader: {
    marginBottom: 16,
  },
  daysContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  dayButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    minWidth: 50,
    alignItems: 'center',
  },
  dayButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  saveButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  clearButton: {
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  clearButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  bottomSpacer: {
    height: 300,
  },
  fullScreenModal: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  fullScreenModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  closeButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullScreenModalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  fullScreenModalContent: {
    padding: 20,
    flexGrow: 1,
  },
  fullScreenModalDescription: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 20,
  },
  fullScreenExitNoteInput: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    fontSize: 15,
    minHeight: 160,
  },
  fullScreenModalFooter: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 40,
    borderTopWidth: 1,
  },
  fullScreenCancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
  },
  fullScreenCancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  fullScreenSubmitButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullScreenSubmitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalDescription: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 20,
  },
  exitNoteInput: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    fontSize: 15,
    minHeight: 120,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    borderWidth: 1.5,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {},
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  codeSection: {
    padding: 16,
  },
  codeDisplayContainer: {
    marginTop: 12,
    gap: 12,
  },
  codeBox: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  codeText: {
    fontSize: 24,
    fontWeight: '700',
    letterSpacing: 2,
  },
  codeActions: {
    flexDirection: 'row',
    gap: 12,
  },
  codeActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1.5,
    gap: 6,
  },
  codeActionText: {
    fontSize: 15,
    fontWeight: '600',
  },
  codeEditContainer: {
    marginTop: 12,
    gap: 12,
  },
  codeInput: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    letterSpacing: 2,
  },
  codeEditButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  codeEditButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  codeEditButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ClubDetailsTab;
